import { FC, KeyboardEvent, ReactNode, useMemo } from 'react'
import Link from 'next/link'
import { Box, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { NEGATIVE_COLORS_VARIANTS, PRIMARY_COLORS_VARIANTS } from '@/utils/constants/colors'

export interface DrawerLinkElementProps {
  icon: ReactNode
  text: string
  link?: string
  hasChevron?: boolean
  isActive?: boolean
  isNegative?: boolean
  onClick?: () => void
}

export const DrawerLinkElement: FC<DrawerLinkElementProps> = ({
  icon,
  text,
  link,
  hasChevron = true,
  isActive = false,
  isNegative,
  onClick,
}) => {
  const bgColor = useMemo(() => {
    if (isActive) return PRIMARY_COLORS_VARIANTS[200]

    return 'transparent'
  }, [isActive])

  const textColor = useMemo(() => {
    if (isActive) return PRIMARY_COLORS_VARIANTS[500]
    if (isNegative) return NEGATIVE_COLORS_VARIANTS[400]

    return 'inherit'
  }, [isNegative, isActive])

  const hoverBgColor = useMemo(() => {
    if (isNegative) return NEGATIVE_COLORS_VARIANTS[200]

    return PRIMARY_COLORS_VARIANTS[200]
  }, [isNegative])

  if (onClick || !link) {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick?.()
      }
    }

    return (
      <Box
        role="button"
        tabIndex={0}
        onClick={isActive ? undefined : onClick}
        onKeyDown={isActive ? undefined : handleKeyDown}
        sx={{
          cursor: isActive ? 'default' : 'pointer',
          pointerEvents: isActive ? 'none' : 'auto',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          py={1}
          px={2}
          borderRadius="6px"
          bgcolor={bgColor}
          color={textColor}
          sx={{
            '&:hover': {
              backgroundColor: hoverBgColor,
            },
            outline: 'none',
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {icon}
            <Typography sx={{ color: 'inherit' }}>{text}</Typography>
          </Box>
          {hasChevron && <ChevronRightIcon />}
        </Box>
      </Box>
    )
  }

  // Default: render a next/link for navigation links
  return (
    <Link
      href={link as string}
      style={{
        cursor: isActive ? 'default' : 'pointer',
        pointerEvents: isActive ? 'none' : 'auto',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        py={1}
        px={2}
        borderRadius="6px"
        bgcolor={bgColor}
        color={textColor}
        sx={{
          '&:hover': {
            backgroundColor: hoverBgColor,
          },
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {icon}
          <Typography sx={{ color: 'inherit' }}>{text}</Typography>
        </Box>
        {hasChevron && <ChevronRightIcon />}
      </Box>
    </Link>
  )
}
